// src/services/phoneService.ts
import { useSettingsStore } from '@/stores/settings'

/**
 * Phone Service - Provides business logic for phone-related operations
 * Handles call URL generation, message URL generation, and phone settings
 */
export function usePhoneService() {
  const settingsStore = useSettingsStore()

  return {
    /**
     * Get phone settings from the database
     */
    getPhoneSettings() {
      return settingsStore.phoneSettings
    },

    /**
     * Generate call URL for a phone number based on settings
     */
    getCallUrl(phone: string): string {
      const settings = settingsStore.phoneSettings

      if (settings.callUrlType === 'default') {
        return `tel:${phone}`
      }

      return settings.callUrlTemplate.replace(/\{\{phone\}\}/g, phone)
    },

    /**
     * Generate message URL for a phone number based on settings
     */
    getMessageUrl(phone: string): string {
      const settings = settingsStore.phoneSettings
      return settings.messageUrlTemplate.replace(/\{\{phone\}\}/g, phone)
    },

    /**
     * Format phone number for display
     */
    formatPhoneNumber(phone: string): string {
      // Remove all non-digit characters
      const digits = phone.replace(/\D/g, '')

      // Format as (XXX) XXX-XXXX for 10-digit numbers
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }

      // Return original if not 10 digits
      return phone
    },

    /**
     * Clean phone number (remove formatting)
     */
    cleanPhoneNumber(phone: string): string {
      return phone.replace(/\D/g, '')
    },

    /**
     * Validate phone number format
     */
    isValidPhoneNumber(phone: string): boolean {
      const cleaned = this.cleanPhoneNumber(phone)
      return cleaned.length >= 10 && cleaned.length <= 15
    },
  }
}
